import type { ProviderVenue } from "../../lib/api";
import { type TranslationFunction, useAppPreferences } from "../../lib/preferences";

function formatLabel(value: string, t: TranslationFunction) {
  return t(value.replaceAll("_", " "));
}

function countText(t: TranslationFunction, count: number) {
  return t(count === 1 ? "{count} unit" : "{count} units", { count });
}

function getStatusTone(status: ProviderVenue["approval_status"]) {
  switch (status) {
    case "approved":
      return "success";
    case "rejected":
    case "removed":
      return "danger";
    case "removal_requested":
      return "warning";
    default:
      return "info";
  }
}

export function VenueList({
  isLoading,
  onSelect,
  selectedVenueId,
  venues,
}: {
  isLoading: boolean;
  onSelect: (venueId: string) => void;
  selectedVenueId: string | null;
  venues: ProviderVenue[];
}) {
  const { t } = useAppPreferences();

  if (!isLoading && venues.length === 0) {
    return (
      <div className="ops-empty px-5 py-6">
        {t("No venues yet. Create the first venue to establish the provider workspace.")}
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      {venues.map((venue) => {
        const isSelected = venue.id === selectedVenueId;

        return (
          <button
            className={`ops-record ops-record-dense ops-interactive-record grid grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-3 text-start transition ${
              isSelected
                ? "border-[var(--color-signal)]/40 bg-[var(--color-signal-muted)]"
                : "hover:border-[var(--color-border)] hover:bg-[var(--color-signal-muted)]"
            }`}
            aria-pressed={isSelected}
            data-active={isSelected}
            key={venue.id}
            onClick={() => onSelect(venue.id)}
            type="button"
          >
            {venue.image_url ? (
              <img
                alt=""
                className="h-14 w-14 shrink-0 rounded-lg object-cover"
                src={venue.image_url}
              />
            ) : (
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-ink)] text-xs font-bold text-slate-400">
                {venue.name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-semibold text-white">{venue.name}</p>
              <p className="truncate text-xs text-slate-400">
                {venue.city} · {venue.timezone}
              </p>
            </div>
            <div className="col-span-2 flex flex-wrap items-center gap-1.5">
              {isSelected && (
                <div className="ops-chip" data-tone="info">
                  {t("Selected")}
                </div>
              )}
              <div className="ops-chip" data-tone={getStatusTone(venue.approval_status)}>
                {formatLabel(venue.approval_status, t)}
              </div>
              <div className="ops-chip">{countText(t, venue.units.length)}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
